import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, Eye, FilePlus2, History, Upload } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { Collaborator, mensagemDeErro } from '../api';
import { CollaboratorDocument, baixarDocumento, visualizarDocumento } from '../api/documentos';
import {
  useCancelarDocumento, useChecklist, useDocumentos, useEnviarDocumento, useTiposDocumentais, useVersoes,
} from '../hooks/cadastro';
import {
  CONFIDENTIALITY_LABELS, DOCUMENT_CATEGORY_LABELS, StatusTone, awaitsReview, canCancelDocument, canReplaceDocument,
  documentStatusTone, formatDate,
} from './format';
import { PessoasModal } from './PessoasModal';
import { ReviewDocumentModal } from './ReviewDocumentModal';
import { UploadDocumentModal } from './UploadDocumentModal';

const TONES: Record<StatusTone, string> = {
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  amber: 'bg-amber-100 text-amber-800',
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-100 text-red-700',
};

export function DocumentStatusBadge({ status, label }: { status: string; label: string }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TONES[documentStatusTone(status)]}`}>{label}</span>;
}

/** Aba Documentos (US-GP-002, seção 19): checklist do vínculo e documentos por categoria. */
export function DocumentosTab({ person }: { person: Collaborator }) {
  const toast = useToast();
  const { can } = usePermissions();
  const openBond = person.bonds.find((b) => !b.closed) ?? person.bonds[0];
  const [history, setHistory] = useState(false);
  const documentos = useDocumentos(person.id, history);
  const checklist = useChecklist(openBond?.id);
  const tipos = useTiposDocumentais('true', can(FEATURES.pessoasDocumentos, 'create'));
  const enviar = useEnviarDocumento(person.id);
  const cancelar = useCancelarDocumento();

  const [upload, setUpload] = useState<{ open: boolean; previous: CollaboratorDocument | null; presetTypeId: string | null }>(
    { open: false, previous: null, presetTypeId: null },
  );
  const [reviewing, setReviewing] = useState<CollaboratorDocument | null>(null);
  const [cancelling, setCancelling] = useState<CollaboratorDocument | null>(null);
  const [justification, setJustification] = useState('');
  const [downloading, setDownloading] = useState<CollaboratorDocument | null>(null);
  const [purpose, setPurpose] = useState('');
  const [versionsOf, setVersionsOf] = useState<string | null>(null);
  const versoes = useVersoes(versionsOf);

  const grupos = useMemo(() => {
    const map = new Map<string, CollaboratorDocument[]>();
    for (const doc of documentos.data ?? []) {
      const key = doc.category ?? 'pessoal';
      map.set(key, [...(map.get(key) ?? []), doc]);
    }
    return [...map.entries()];
  }, [documentos.data]);

  const podeValidar = can(FEATURES.pessoasDocumentosValidacao, 'edit');
  const podeBaixar = can(FEATURES.pessoasDocumentosExportacao, 'view');
  const podeEnviar = can(FEATURES.pessoasDocumentos, 'create');
  const podeEditar = can(FEATURES.pessoasDocumentos, 'edit');

  async function abrir(doc: CollaboratorDocument) {
    try {
      await visualizarDocumento(doc.id);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível abrir o documento.'));
    }
  }

  async function baixar(doc: CollaboratorDocument, finalidade?: string) {
    try {
      await baixarDocumento(doc, finalidade);
      setDownloading(null);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível baixar o documento.'));
    }
  }

  async function confirmarCancelamento() {
    if (!cancelling) return;
    try {
      await cancelar.mutateAsync({ id: cancelling.id, justification: justification.trim() });
      toast.success('Documento cancelado.');
      setCancelling(null);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível cancelar o documento.'));
    }
  }

  const checklistItens = checklist.data ?? [];
  const satisfeitos = checklistItens.filter((i) => i.satisfied).length;

  return (
    <div className="space-y-5">
      {openBond && checklistItens.length > 0 && (
        <section className="rounded-md bg-gray-50 p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            Checklist do vínculo {openBond.internalRegistration} — {satisfeitos}/{checklistItens.length} entregues
          </h3>
          <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {checklistItens.map((item) => (
              <li key={item.type.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2">
                  {item.satisfied
                    ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                    : <AlertTriangle className={`h-4 w-4 ${item.blocksAdmission ? 'text-red-500' : 'text-amber-500'}`} />}
                  {item.type.name}
                  {item.blocksAdmission && !item.satisfied && <span className="text-xs text-red-600">(impede a admissão)</span>}
                </span>
                {!item.satisfied && podeEnviar && !item.document && (
                  <button
                    type="button"
                    className="text-xs font-medium text-blue-700 hover:underline"
                    onClick={() => setUpload({ open: true, previous: null, presetTypeId: item.type.id })}
                  >
                    Enviar
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} />
          Mostrar substituídos e cancelados
        </label>
        <Can feature={FEATURES.pessoasDocumentos} action="create">
          <Button size="sm" onClick={() => setUpload({ open: true, previous: null, presetTypeId: null })}>
            <Upload className="mr-1 h-4 w-4" /> Enviar documento
          </Button>
        </Can>
      </div>

      {documentos.isLoading ? (
        <p className="text-sm text-gray-500">Carregando documentos…</p>
      ) : grupos.length === 0 ? (
        <EmptyState size="compact" title="Nenhum documento" description="Os documentos enviados aparecem aqui, por categoria." />
      ) : (
        grupos.map(([categoria, docs]) => (
          <section key={categoria}>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">{DOCUMENT_CATEGORY_LABELS[categoria] ?? categoria}</h3>
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Documento</th>
                    <th className="px-3 py-2">Situação</th>
                    <th className="px-3 py-2">Validade</th>
                    <th className="px-3 py-2">Confidencialidade</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {docs.map((doc) => (
                    <tr key={doc.id}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900">{doc.typeName}</p>
                        <p className="text-xs text-gray-500">
                          v{doc.version} · {doc.fileName} · enviado em {formatDate(doc.uploadedAt)}
                        </p>
                        {doc.rejectionComment && (
                          <p className="text-xs text-red-700">
                            {doc.rejectionReasonLabel ? `${doc.rejectionReasonLabel}: ` : ''}{doc.rejectionComment}
                          </p>
                        )}
                        {doc.validationNote && <p className="text-xs text-amber-700">Ressalva: {doc.validationNote}</p>}
                      </td>
                      <td className="px-3 py-2"><DocumentStatusBadge status={doc.status} label={doc.statusLabel} /></td>
                      <td className="px-3 py-2 text-gray-700">{formatDate(doc.expiresAt)}</td>
                      <td className="px-3 py-2 text-gray-700">{CONFIDENTIALITY_LABELS[doc.confidentiality]}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <button type="button" title="Visualizar" onClick={() => abrir(doc)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100">
                            <Eye className="h-4 w-4" />
                          </button>
                          {podeBaixar && (
                            <button
                              type="button"
                              title="Baixar"
                              onClick={() => (doc.confidentiality === 'interno' ? baixar(doc) : (setPurpose(''), setDownloading(doc)))}
                              className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          )}
                          {doc.version > 1 || doc.previousVersionId ? (
                            <button type="button" title="Versões" onClick={() => setVersionsOf(doc.id)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100">
                              <History className="h-4 w-4" />
                            </button>
                          ) : null}
                          {podeEnviar && canReplaceDocument(doc.status) && (
                            <button
                              type="button"
                              title="Nova versão"
                              onClick={() => setUpload({ open: true, previous: doc, presetTypeId: null })}
                              className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                            >
                              <FilePlus2 className="h-4 w-4" />
                            </button>
                          )}
                          {podeValidar && awaitsReview(doc.status) && (
                            <Button size="sm" onClick={() => setReviewing(doc)}>Analisar</Button>
                          )}
                          {podeEditar && canCancelDocument(doc.status) && (
                            <Button size="sm" variant="ghost" onClick={() => { setJustification(''); setCancelling(doc); }}>Cancelar</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      <UploadDocumentModal
        open={upload.open}
        mode="dp"
        types={tipos.data ?? []}
        bonds={person.bonds}
        previous={upload.previous}
        presetTypeId={upload.presetTypeId}
        submitting={enviar.isPending}
        onClose={() => setUpload({ open: false, previous: null, presetTypeId: null })}
        onSubmit={async (payload) => {
          await enviar.mutateAsync(payload);
          setUpload({ open: false, previous: null, presetTypeId: null });
          toast.success('Documento enviado para validação.');
        }}
      />

      <ReviewDocumentModal document={reviewing} onClose={() => setReviewing(null)}
        onDone={(m) => { setReviewing(null); toast.success(m); }} />

      <PessoasModal
        open={cancelling !== null}
        title="Cancelar documento?"
        description="O documento sai dos processos, mas o arquivo e o histórico são preservados."
        submitLabel="Cancelar documento"
        submitVariant="danger"
        isSubmitting={cancelar.isPending}
        canSubmit={justification.trim().length > 0}
        onClose={() => setCancelling(null)}
        onSubmit={confirmarCancelamento}
      >
        <Textarea label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} />
      </PessoasModal>

      <PessoasModal
        open={downloading !== null}
        title="Baixar documento restrito"
        description="Informe a finalidade. O download fica registrado na trilha de auditoria."
        submitLabel="Baixar"
        canSubmit={purpose.trim().length > 0}
        onClose={() => setDownloading(null)}
        onSubmit={() => downloading && baixar(downloading, purpose.trim())}
      >
        <TextInput label="Finalidade" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      </PessoasModal>

      <PessoasModal
        open={versionsOf !== null}
        title="Histórico de versões"
        submitLabel="Fechar"
        onClose={() => setVersionsOf(null)}
        onSubmit={() => setVersionsOf(null)}
      >
        <ol className="space-y-2">
          {(versoes.data ?? []).map((v) => (
            <li key={v.id} className="flex items-center justify-between text-sm">
              <span>Versão {v.version} · {formatDate(v.uploadedAt)} · {v.fileName}</span>
              <DocumentStatusBadge status={v.status} label={v.statusLabel} />
            </li>
          ))}
        </ol>
      </PessoasModal>
    </div>
  );
}
